import React, { useState } from 'react';
import { Loader } from '../loader';
import { VALIDATE_VOUCHER } from './vouchorMutation';
import { productStoreApi, T_VC_DeviceType, useToastContainer } from '@sky-tv-group/shared';
import { T_PromoCodeInput } from './promoCodeTypes';

import {
  SPLITIO_KEY,
  useCouponStore,
  useSplitIO,
  T_VC_ProductType,
  T_Voucher,
  T_OrderRequest,
  useOrderStore,
  useCustomerStore,
  useProductStore,
  couponStoreApi,
  NSB_SKYPOD_MONTHLY_AND_ONEOFF_FEE_ON_US,
  CREDIT_OFFER,
  useAnalytics,
  VoucherEvent,
} from '@sky-tv-group/shared';
import { Bin } from '../running-total/RunningTotalIcons';
import '../style.css';
import { getCartProductAndDeviceType } from '../review/charges/helpers';

export function PromoCode({ konakartService, client, journeyType }: T_PromoCodeInput) {
  const { voucher } = useCouponStore();
  return (
    <>
      {voucher && voucher.isVoucher ? (
        <PromoCodeOfferText />
      ) : (
        <PromoCodeInput konakartService={konakartService} client={client} journeyType={journeyType} />
      )}
    </>
  );
}
export const PromoCodeInput = ({ konakartService, client, journeyType }: T_PromoCodeInput) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [voucherCode, setVoucherCode] = useState('');
  const { setPromoCodeVoucher, setPreviousStateCoupons, getSplitCoupons, coupons } = useCouponStore();
  const isBroadband = journeyType === T_VC_ProductType.BROADBAND;
  let errorMsg = 'Invalid promo code';
  const { updateOrder, order } = useOrderStore();
  const { trackEvent } = useAnalytics();
  const { customerId } = useCustomerStore(s => ({
    customerId: s.kk,
  }));
  const { products } = useProductStore();
  const { removeAllToasts } = useToastContainer();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVoucherCode(e.target.value.replace(' ', ''));
    setErrorMessage(null);
  };

  const removeVoucherState = () => {
    setVoucherCode('');
    setErrorMessage(null);
  };
  const handleApplyPromoCoupon = async () => {
    removeAllToasts();
    setIsLoading(true);
    const { calculatedJourneyType, calculatedDeviceType } = getCartProductAndDeviceType(order);

    try {
      const { data } = await client.mutate({
        mutation: VALIDATE_VOUCHER,
        variables: { voucherCode, journeyType: calculatedJourneyType, deviceType: T_VC_DeviceType.ANY },
      });

      const { validateVoucher } = data || {};

      if (validateVoucher?.RequestStatus === 'Failure' || validateVoucher?.RequestStatus === 'Failure') {
        trackEvent(VoucherEvent.voucher_apply_clicked, {
          voucherCoupon: '',
          voucherCode: voucherCode ?? '',
          alertMessage: errorMsg,
        });
        // const isOnGetSkyPage = ICE_URL && window.location.origin === new URL(ICE_URL).origin;
        // if (validateVoucher?.productType === T_VC_ProductType.DTH_AND_BROADBAND && (calculatedJourneyType === T_VC_ProductType.BROADBAND || calculatedJourneyType === T_VC_ProductType.DTH)) {
        //   errorMsg = isOnGetSkyPage ? `This voucher is valid only for TV + Broadband combo products. Please add both TV and Broadband to proceed.`
        //     : `This voucher is valid only for TV + Broadband combo products.  <a href="${ICE_URL}" target="_blank" rel="noopener noreferrer">Click here</a> to add both products together and proceed.`;
        // }
        // else if (validateVoucher?.productType === T_VC_ProductType.BROADBAND && calculatedJourneyType === T_VC_ProductType.DTH) {
        //   errorMsg = isOnGetSkyPage ? `This voucher is valid for Broadband products.Please add both TV and Broadband to proceed.`
        //     : `This voucher is valid for Broadband products. <a href="${ICE_URL}">Click here</a> to add Broadband with TV as a combo, or <a href="${BROADBAND_SPA_URL}">click here</a> to continue with Broadband only.`;
        // }
        // else if (validateVoucher?.productType === T_VC_ProductType.DTH && calculatedJourneyType === T_VC_ProductType.BROADBAND) {
        //   errorMsg =`This voucher is valid for DTH products. <a href="${ICE_URL}">Click here</a> to add DTH with Broadband as a combo, or you can to continue with DTH only.`;
        // }
        setErrorMessage(errorMsg);
        setIsLoading(false);
        return;
      }

      const {
        Campaigns: campaignCodes,
        ServiceCodes: skus,
        OfferId: offerId,
        OfferDescription: description,
        ProductType: productType,
        DeviceType: deviceType,
        url,
      } = validateVoucher;

      // Set voucher object in store
      const voucherObj: T_Voucher = {
        voucherCode,
        coupon: campaignCodes,
        skus,
        offerId,
        description,
        url,
        productType,
        deviceType,
        isVoucher: false,
        isRemovalModal: false,
      };
      setPromoCodeVoucher(voucherObj);

      // Build initial order request
      let orderRequest: T_OrderRequest[] = order?.orderProducts?.length
        ? order.orderProducts.map((p: any) => ({
            productId: p.productId,
            quantity: p.quantity,
            finalPriceIncTax: p.finalPriceIncTax,
            downgrade: p.downgrade ?? false,
            inPromotion: p.inPromotion,
            currentlySubscribed: false,
            occurrenceNumber: p.occurrenceNumber,
            broadbandPriceHold: p.broadbandPriceHold,
            isPhantom: p.isPhantom,
          }))
        : [];

      // Save existing coupons, clear store
      setPreviousStateCoupons(couponStoreApi.getState().coupons);
      couponStoreApi.getState().removeAllCoupons();

      let voucherFinalCampaignCodes = campaignCodes;
      // Fetch validated campaign coupons
      const fetchedCoupons = voucherFinalCampaignCodes?.length
        ? await Promise.all(voucherFinalCampaignCodes.map((cp: string) => konakartService.getCoupon(cp)))
        : [];

      const { dthCoupon: previousDTHCoupon, bbCoupon: previousBBCoupon, oneOffFee } = getSplitCoupons(coupons); // Split Existing Coupons to get DTH coupon if exists

      // Conditionally add extra coupon
      const has04998 = await konakartService.getCoupon(NSB_SKYPOD_MONTHLY_AND_ONEOFF_FEE_ON_US);
      let couponsToApply = journeyType !== T_VC_ProductType.BROADBAND ? [has04998, ...fetchedCoupons] : fetchedCoupons;
      if (productType === T_VC_ProductType.BROADBAND && calculatedJourneyType === T_VC_ProductType.DTH_AND_BROADBAND) {
        couponsToApply = [...fetchedCoupons, ...(previousDTHCoupon ?? []), oneOffFee ? has04998 : null];
      }
      if (productType === T_VC_ProductType.DTH && calculatedJourneyType === T_VC_ProductType.DTH_AND_BROADBAND) {
        couponsToApply = [...fetchedCoupons, ...(previousBBCoupon ?? []), oneOffFee ? has04998 : null];
      }

      // Apply coupons to store
      couponStoreApi.getState().applyMultipleCouponsToStore(couponsToApply);

      // Find matching product SKUs
      const productIds = products.filter(item => couponStoreApi.getState()?.voucher?.skus?.includes(item.sku));
      const orderRequestIds = orderRequest.map(p => p.productId);

      const serviceCodesAdded = productIds
        .filter(p => !orderRequestIds.includes(p.id))
        .map(p => ({
          productId: p.id,
          quantity: 1,
          finalPriceIncTax: p.custom9 === CREDIT_OFFER ? -p.price1 : p.price0,
          downgrade: p.downgrade ?? false,
          inPromotion: p.custom9 === CREDIT_OFFER ? true : p.inPromotion ?? false,
          currentlySubscribed: false,
          occurrenceNumber: p.occurrenceNumber,
          broadbandPriceHold: p.broadbandPriceHold ?? false,
          isPhantom: p.isPhantom ?? false,
        }));

      let voucherProductId;

      if (serviceCodesAdded.length > 0) {
        voucherProductId = serviceCodesAdded.map(p => p.productId);
        orderRequest = [...orderRequest, ...serviceCodesAdded];
      }
      trackEvent(VoucherEvent.voucher_apply_clicked, {
        voucherCoupon: voucherObj?.coupon ?? '',
        voucherCode: voucherObj?.voucherCode ?? '',
      });

      // Submit updated order with applied coupons
      await updateOrder(
        konakartService,
        customerId,
        orderRequest,
        couponStoreApi.getState()?.coupons,
        undefined,
        couponStoreApi.getState()?.ctCoupons,
        productStoreApi.getState()?.products
      );

      // Update voucher state with applied product IDs
      setPromoCodeVoucher({ ...voucherObj, isVoucher: true, voucherProductId });
      setIsOpenModal(false);
    } catch (err) {
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`w-full ${isBroadband ? 'mt-5 mb-7 pt-2' : 'mt-5'}`}>
      <label className="sky-h4-black mb-2 mr-3">Promo Code :</label>
      {isLoading ? (
        <div className="block lg:inline md:inline ">
          <div className="w-full h-4 relative">
            <Loader bgOpacity={false} />
          </div>
        </div>
      ) : (
        <>
          <span className={`block mt-2 ${!isBroadband && 'lg:inline md:inline'}`}>
            <span className="relative mr-1">
              <input
                className={`${isBroadband ? 'w-11/12 md:w-11/12' : 'w-4/6 md:w-7/12 '} lg:w-6/12  ${
                  errorMessage ? `sky-input--error` : ''
                } sky-input sky-input--text sky-h7-reg rounded`}
                onChange={handleInputChange}
                maxLength={12}
                value={voucherCode}
              />
              {errorMessage && (
                <span className="absolute cursor-pointer" style={{ right: '10px', bottom: '-3px' }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    onClick={removeVoucherState}>
                    <circle cx="12.375" cy="11.625" r="9.375" fill="white" />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M12.375 21C17.5527 21 21.75 16.8027 21.75 11.625C21.75 6.44733 17.5527 2.25 12.375 2.25C7.19733 2.25 3 6.44733 3 11.625C3 16.8027 7.19733 21 12.375 21ZM16.0524 9.05243C16.3575 8.74733 16.3575 8.25267 16.0524 7.94757C15.7473 7.64248 15.2527 7.64248 14.9476 7.94757L12.375 10.5201L9.80243 7.94757C9.49733 7.64248 9.00267 7.64248 8.69758 7.94757C8.39248 8.25267 8.39248 8.74733 8.69758 9.05243L11.2701 11.625L8.69757 14.1976C8.39248 14.5027 8.39248 14.9973 8.69757 15.3024C9.00267 15.6075 9.49733 15.6075 9.80243 15.3024L12.375 12.7299L14.9476 15.3024C15.2527 15.6075 15.7473 15.6075 16.0524 15.3024C16.3575 14.9973 16.3575 14.5027 16.0524 14.1976L13.4799 11.625L16.0524 9.05243Z"
                      fill="#0057FF"
                    />
                  </svg>
                </span>
              )}
            </span>
            {errorMessage && isBroadband && (
              <div className="lg:hidden md:block block">
                <p className={`text-error`}>{errorMessage}</p>
              </div>
            )}
            <button
              disabled={errorMessage || voucherCode.length === 0 ? true : false}
              className={`sky-button sky-button--secondary-pure ${errorMessage ? 'gray' : ' '} px-1 ${isBroadband &&
                'w-11/12 mt-4 lg:mt-0 md:mt-4'} lg:w-1/5`}
              id="agreeAndProceed"
              onClick={handleApplyPromoCoupon}>
              Apply
            </button>
          </span>
          {errorMessage && (
            <div>
              <p
                className={`text-error ${
                  !isBroadband ? 'lg:ml-4 md:ml-4 lg:pl-32 md:pl-32' : 'lg:block md:hidden hidden'
                }`}>
                {errorMessage}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export function PromoCodeOfferText() {
  const { voucher, setPromoCodeVoucher } = useCouponStore();
  const [isVoucherEnabled, isVoucherOffer] = useSplitIO(SPLITIO_KEY.SKYWEB_VOUCHER_ENABLE);
  const voucherOfferDescription = isVoucherOffer && JSON.parse(isVoucherOffer as any);

  return (
    <div className="p-2 flex gap-2">
      <div
        className="pt-1 cursor-pointer"
        onClick={() => setPromoCodeVoucher({ ...voucher, isRemovalModal: true } as T_Voucher)}>
        <Bin />
      </div>
      <div>
        <p className="sky-h4-black promoCouponColor">Promo Code Applied</p>
        {isVoucherEnabled && voucherOfferDescription && (
          <div className="sky-h7-reg">
            {voucher?.description}{' '}
            {voucher?.url && voucher?.url !== '' && (
              <>
                <a
                  href={voucher?.url ?? '/'}
                  target="blank"
                  className="underline cursor-pointer text-primary-day-light">
                  {' '}
                  T&Cs
                </a>{' '}
                apply.{' '}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
